import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trophy, Calendar } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "../lib/api";

const LOGO_URL =
  "https://www.ontour.scootive.pl/img/logo.svg";

function formatDateRange(c) {
  const fmt = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return null;
    }
  };
  const end = fmt(c.ends_at || c.archived_at);
  const start = fmt(c.created_at);
  if (start && end) return `${start} → ${end}`;
  return end || start || "—";
}

export default function ArchivePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    api
      .get("/campaigns/archive")
      .then(({ data }) => setItems(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col text-black"
      style={{
        background:
          "radial-gradient(120% 60% at 50% -10%, rgba(46,108,46,0.10) 0%, rgba(46,108,46,0.04) 30%, rgba(250,250,250,1) 60%)",
      }}
    >
      <main className="flex-1 w-full max-w-3xl mx-auto px-5 sm:px-6 pt-10 sm:pt-14 pb-10">
        <div className="flex flex-col items-center text-center mb-10 rise-in rise-1">
          <a
            href="https://www.scootive.pl"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <img src={LOGO_URL} alt="Scootive" className="h-9 sm:h-10" />
          </a>
          <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.4em] text-black/35">
            Archiwum
          </p>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">
            Poprzednie kampanie
          </h1>
          <p className="mt-4 text-[15px] text-black/60 max-w-md leading-relaxed">
            Zobacz wyniki dotychczasowych tournée Scootive — gdzie byliśmy, gdzie nas wybraliście.
          </p>
        </div>

        {loading && (
          <p className="text-center text-black/40 text-sm">Ładowanie…</p>
        )}

        {!loading && items.length === 0 && (
          <div className="surface-soft p-10 text-center">
            <p className="text-black/50 text-sm">Brak zarchiwizowanych kampanii.</p>
          </div>
        )}

        <div className="space-y-6 rise-in rise-3">
          {items.map(({ campaign, leaderboard, total_votes }) => (
            <article
              key={campaign.id}
              data-testid={`archive-campaign-${campaign.id}`}
              className="surface-card p-6 sm:p-8"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                <div className="min-w-0">
                  <p className="text-[10px] font-medium uppercase tracking-[0.3em] brand-green flex items-center gap-2 mb-1">
                    <Calendar className="w-3 h-3" /> {formatDateRange(campaign)}
                  </p>
                  <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-tight">
                    {campaign.title}
                  </h2>
                  {campaign.subtitle && (
                    <p className="text-black/55 text-sm mt-1">{campaign.subtitle}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-black/40">
                    Łącznie głosów
                  </p>
                  <p className="font-display text-3xl brand-green leading-none tabular-nums">
                    {total_votes}
                  </p>
                </div>
              </div>

              {leaderboard.length === 0 ? (
                <p className="text-black/45 text-sm text-center py-6">
                  Brak głosów w tej kampanii
                </p>
              ) : (
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-black/50 mb-3">
                    {campaign.results_title || "Ranking"} · TOP {leaderboard.length}
                  </p>
                  <div className="space-y-1.5">
                    {leaderboard.map((row, idx) => {
                      const max = leaderboard[0]?.votes || 1;
                      const pct = Math.max(3, (row.votes / max) * 100);
                      const isTop = idx === 0;
                      return (
                        <div
                          key={row.skatepark_id}
                          className={`relative rounded-xl ${isTop ? "ring-2 ring-[#2e6c2e]" : "ring-1 ring-black/5"} bg-white overflow-hidden`}
                        >
                          <div
                            className="absolute inset-y-0 left-0 bg-[#2e6c2e]/8"
                            style={{ width: `${pct}%` }}
                          />
                          <div className="relative flex items-center justify-between gap-3 px-3 py-2.5">
                            <div className="flex items-center gap-3 min-w-0">
                              <span
                                className={`font-display text-xl leading-none ${isTop ? "brand-green" : "text-black/35"} w-7 shrink-0 tabular-nums`}
                              >
                                {String(idx + 1).padStart(2, "0")}
                              </span>
                              {isTop && <Trophy className="w-4 h-4 brand-green shrink-0" />}
                              <div className="min-w-0">
                                <p className="font-semibold text-sm sm:text-base truncate">{row.city}</p>
                                {row.skatepark && (
                                  <p className="text-[10px] sm:text-[11px] text-black/45 truncate">
                                    {row.skatepark}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-display text-xl leading-none brand-green tabular-nums">
                                {row.votes}
                              </p>
                              <p className="text-[9px] text-black/45 tabular-nums">{row.percentage}%</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <Link
            to="/"
            data-testid="back-home-link"
            className="btn-ghost-brand inline-flex items-center gap-2 px-6 h-11 text-xs font-medium tracking-wide"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Wróć do głosowania
          </Link>
        </div>

        <p className="mt-10 text-center text-[11px] text-black/35">
          © {currentYear} Scootive
        </p>
      </main>
    </div>
  );
}
